import { Fragment, useEffect, useState } from "react";

import { getProgram, getPrograms, type ProgramCard, type ProgramDetail } from "../lib/api";
import { plural } from "../lib/format";
import { haptic, useNativeBack } from "../lib/telegram";
import BrandBlock from "../components/BrandBlock";
import ScreenHead from "../components/ScreenHead";
import ProgramTile from "../components/ProgramTile";
import { BusyLabel } from "../components/DotLoader";
import { useSession } from "../store/session";

interface Props {
  onPickDay: (dayId: number) => void;
  onFreeWorkout: () => void;
  onBack: () => void;
}

/**
 * Выбор, с чего начать тренировку: день программы или свободная.
 *
 * Два шага, а не один длинный список: у заказчика 13 программ и до 32 дней в
 * каждой — плоский перечень из четырёхсот строк непроходим.
 */
export default function StartWorkout({ onPickDay, onFreeWorkout, onBack }: Props) {
  const [programs, setPrograms] = useState<ProgramCard[] | null>(null);
  const [opened, setOpened] = useState<ProgramDetail | null>(null);
  const [blockIndex, setBlockIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const starting = useSession((s) => s.starting);
  const startError = useSession((s) => s.startError);

  useEffect(
    () => useNativeBack(opened ? () => setOpened(null) : onBack),
    [opened, onBack],
  );

  useEffect(() => {
    getPrograms()
      .then(setPrograms)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  async function open(id: number) {
    haptic();
    try {
      const detail = await getProgram(id);
      setBlockIndex(0);
      setOpened(detail);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  if (opened) {
    const block = opened.blocks[blockIndex];
    return (
      <div className="screen">
        <ScreenHead eyebrow="С чего начнём" title={opened.name} seed={5} />

        {opened.blocks.length > 1 && (
          <div className="chips" role="tablist" aria-label="Блоки программы">
            {opened.blocks.map((b, i) => (
              <button
                key={b.id}
                role="tab"
                aria-selected={i === blockIndex}
                className={`chip ${i === blockIndex ? "chip--active" : ""}`}
                onClick={() => {
                  haptic();
                  setBlockIndex(i);
                }}
              >
                {b.name}
              </button>
            ))}
          </div>
        )}

        <div className="stack">
          {block?.days.map((day) => (
            <Fragment key={day.id}>
              {/* Старт — запрос к серверу, на плохой связи это секунды. Без
                  отклика на карточке её жмут ещё раз или уходят назад. */}
              <button
                className={`daypick ${starting === day.id ? "daypick--starting" : ""}`}
                disabled={starting !== null}
                onClick={() => {
                  haptic("medium");
                  onPickDay(day.id);
                }}
              >
                <span className="daypick__name">
                  {starting === day.id ? <BusyLabel text="Начинаем" /> : day.name}
                </span>
                <span className="daypick__meta">
                  {day.exercises
                    .slice(0, 3)
                    .map((e) => e.exercise.name_ru)
                    .join(" · ")}
                  {day.exercises.length > 3 && ` · ещё ${day.exercises.length - 3}`}
                </span>
              </button>
              {startError?.day === day.id && (
                <p className="lead lead--error" role="alert" style={{ margin: 0 }}>
                  {startError.message}
                </p>
              )}
            </Fragment>
          ))}
          {block?.days.length === 0 && (
            <p className="lead" style={{ margin: 0 }}>
              В этом блоке нет тренировок.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <ScreenHead eyebrow="Начать тренировку" title="Выбери программу" seed={1} />

      {error && <p className="lead lead--error">{error}</p>}
      {programs === null && !error && (
        <div className="stack">
          {[0, 1].map((i) => (
            <div key={i} className="skeleton skeleton--card" />
          ))}
        </div>
      )}

      <div className="stack">
        {programs?.map((p, i) => (
          <ProgramTile
            key={p.id}
            program={p}
            index={i}
            meta={plural(p.day_count, "тренировка", "тренировки", "тренировок")}
            onOpen={() => void open(p.id)}
          />
        ))}
      </div>

      {/* Свободная тренировка — не запасной путь, а обычный: в зал приходят и
          без программы, и отказ записать такую тренировку выгонит в блокнот. */}
      <BrandBlock
        tone="black"
        seed={7}
        eyebrow="Без программы"
        title="Свободная тренировка"
        caption={
          starting === "free" ? (
            <BusyLabel text="Начинаем" />
          ) : (
            "Записывай подходы как есть — упражнения добавишь по ходу."
          )
        }
        onClick={onFreeWorkout}
      />
      {startError?.day === "free" && (
        <p className="lead lead--error" role="alert" style={{ margin: 0 }}>
          {startError.message}
        </p>
      )}
    </div>
  );
}
